import { action, computed, makeObservable, observable } from 'mobx';
import { decodeHTML } from '../helpers/html-helper';
import { MainStore } from './MainStore';
import { IArticleSetItem, IMixedMediaOfBrick } from './types';

export class MixedMediaStore {
    mainStore: MainStore;

    altTagForFlexibleSetImage: string = '';
    flexibleSetImageFullPath: string = '';

    // @observables:
    displayMediaOverlay: boolean = false;
    mediaOverlayImages: { mainImgIndex: number; imgPath: string }[] = [];
    mixedMediaBricks: IMixedMediaOfBrick[] = [];
    detailsModalOpen: boolean = false;
    masterArticleNo: number | null = null;
    brickNo: number | null = null;
    private mediaOverlayImageIndex: number = 1;

    constructor(mainStore: MainStore) {
        this.mainStore = mainStore;
        this.mixedMediaBricks = mainStore.dataStore.mixedMedia.sortedMixedMediaForBricks;
        this.altTagForFlexibleSetImage = mainStore.dataStore.mixedMedia.altTagForFlexibleSetImage;
        this.flexibleSetImageFullPath = mainStore.dataStore.mixedMedia.flexibleSetImageFullPath;

        this.collectMediaOverlayImages();

        makeObservable(this, {
            displayMediaOverlay: observable,
            mediaOverlayImages: observable,
            mixedMediaBricks: observable,
            detailsModalOpen: observable,
            getMixedMediaImages: computed,
            getSetImage: computed,
            updateMediaOverlayVisibility: action,
            setDetailsModalOpen: action,
            getMediaOverlayImageIndex: computed,
            getImageCount: computed,
            collectMediaOverlayImages: action
        });
    }

    collectMediaOverlayImages(): void {
        let mainImgIndex = 1;
        this.mediaOverlayImages = []; // clean up before new images

        // collect all the images for media overlay:
        this.mixedMediaBricks.forEach(brick => {
            // main image
            if (brick.imageFullPath) {
                this.mediaOverlayImages.push(
                    { mainImgIndex: mainImgIndex, imgPath: brick.imageFullPath }
                );
            }

            if (brick.additionalInfoImagesFullPaths) {
                brick.additionalInfoImagesFullPaths.forEach((image) => {
                    this.mediaOverlayImages.push({ mainImgIndex: mainImgIndex, imgPath: image });
                });
            }

            if (brick.mainActionImageFullPath) {
                this.mediaOverlayImages.push({ mainImgIndex: mainImgIndex, imgPath: brick.mainActionImageFullPath });
            }

            if (brick.secondaryImageFullPath) {
                this.mediaOverlayImages.push({ mainImgIndex: mainImgIndex, imgPath: brick.secondaryImageFullPath });
            }

            mainImgIndex++;
        });
    }

    updateMediaOverlayVisibility(value, mainImgIndex = 1) {
        this.mediaOverlayImageIndex =
            this.mediaOverlayImages.findIndex(it => it.mainImgIndex === mainImgIndex);
        this.displayMediaOverlay = value;
    }

    setDetailsModalOpen(value: boolean, masterArticleNo?: number, brickNo?: number) {
        if (masterArticleNo) {
            this.masterArticleNo = masterArticleNo;
        }
        if (brickNo) {
            this.brickNo = brickNo;
        }
        this.detailsModalOpen = value;
    }

    getArticleSetItem(): IArticleSetItem | undefined {
        const articleSetItems = this.mainStore.dataStore
            .shutterModule.productInfo.description.sortedArticleSetItemDescriptions;
        const selectedArticleSetItem = articleSetItems.find((item) => {
            return (item.masterArticleNo === this.masterArticleNo)
                && (item.brickNo === this.brickNo);
        })
        return selectedArticleSetItem;
    }

    private replaceImageScales(
        bricks: IMixedMediaOfBrick[],
        indexesToUpdate: number[],
        scale: string,
        additionalImgScale: string,
        srcScale: string = 'Original'
    ): IMixedMediaOfBrick[] {
        const scalesMatch = scale === additionalImgScale;
        const shouldUpdateAll = !indexesToUpdate || indexesToUpdate.length === 0;
        return bricks.map((brick, index) => {
            if (!shouldUpdateAll && !indexesToUpdate.includes(index)) {
                // keep brick untouched
                return brick;
            }
            const updatedBrick: IMixedMediaOfBrick = {
                ...brick,
                imageFullPath: brick.imageFullPath?.replace(srcScale, scale),
            };

            if (scalesMatch) {
                return updatedBrick;
            }

            updatedBrick.mainActionImageFullPath =
                updatedBrick.mainActionImageFullPath?.replace(srcScale, additionalImgScale);

            updatedBrick.shoeSoleImageFullPath =
                updatedBrick.shoeSoleImageFullPath?.replace(srcScale, additionalImgScale);

            updatedBrick.secondaryImageFullPath =
                updatedBrick.secondaryImageFullPath?.replace(srcScale, additionalImgScale);

            updatedBrick.additionalInfoImagesFullPaths = updatedBrick.additionalInfoImagesFullPaths?.map((image) => {
                return image.replace(srcScale, additionalImgScale);
            });

            return updatedBrick;
        })
    };

    get getMediaOverlayImageIndex(): number {
        return this.mediaOverlayImageIndex;
    }

    get getMixedMediaImages(): IMixedMediaOfBrick[] {
        const hasSetImage = this.flexibleSetImageFullPath ? 1 : 0;
        let bricksWithImages = this.getBricksWithImages();
        const elementCount = bricksWithImages.length + (hasSetImage ? 1 : 0);

        if (elementCount < 3) {
            bricksWithImages = this.replaceImageScales(bricksWithImages, [], 'FullHeight', 'FullHeight');
            return bricksWithImages;
        }

        switch (elementCount) {
            case 3:
                bricksWithImages = this.replaceImageScales(bricksWithImages, [], 'HalfTile', 'FullHeight');
                if (!hasSetImage) {
                    bricksWithImages = this.replaceImageScales(
                        bricksWithImages,
                        [0],
                        'FullHeight',
                        'FullHeight',
                        'HalfTile'
                    );
                }
                break;

            case 4:
                bricksWithImages = this.replaceImageScales(bricksWithImages, [], 'HalfTile', 'FullHeight');
                break;

            case 5:
                bricksWithImages = this.replaceImageScales(bricksWithImages, [], 'ThirdTile', 'FullHeight');
                if (!hasSetImage) {
                    bricksWithImages = this.replaceImageScales(
                        bricksWithImages,
                        [0, 1],
                        'HalfTile',
                        'FullHeight',
                        'ThirdTile'
                    );
                }
                break;

            case 6:
                bricksWithImages = this.replaceImageScales(bricksWithImages, [], 'ThirdTile', 'FullHeight');
                break;

            case 7:
                bricksWithImages = this.replaceImageScales(bricksWithImages, [], 'SmallTile', 'HalfTile');
                if (!hasSetImage) {
                    bricksWithImages = this.replaceImageScales(
                        bricksWithImages, [0, 1, 2],
                        'ThirdTile',
                        'FullHeight',
                        'SmallTile'
                    );
                }
                break;

            default:
                // elementCount > 7
                bricksWithImages = this.replaceImageScales(bricksWithImages, [], 'SmallTile', 'HalfTile');
                break;
        }

        return bricksWithImages;
    }

    get getSetImage() {
        const setImage = this.flexibleSetImageFullPath;
        const elementCount = this.getImageCount;

        if (!setImage) {
            return null;
        }

        if (elementCount < 4) {
            return setImage.replace('Original', 'FullHeight');
        }

        switch (elementCount) {
            case 4:
            case 5:
                return setImage.replace('Original', 'HalfTile');
            case 6:
            case 7:
                return setImage.replace('Original', 'ThirdTile');
            default:
                // elementCount > 7
                return setImage.replace('Original', 'SmallTile');
        }
    }

    get getImageCount(): number {
        const bricksWithImages = this.getBricksWithImages();
        const setImage = this.flexibleSetImageFullPath;
        const count = bricksWithImages.length + (setImage ? 1 : 0);
        return count;
    }

    getAltValue(index = 0): string {
        if (index > 0) return decodeHTML(this.mixedMediaBricks[index].altTagForImages) + ' ' + index;

        return decodeHTML(this.mixedMediaBricks[index].altTagForImages) ?? '';
    }

    getImagesForCarousel(isSmall: boolean): MixedMediaForCarousel[] {
        const bricksWithImages = this.getBricksWithImages();

        const gridSize = isSmall ? 6 : 9;
        const carouselImages: MixedMediaImage[] = [];

        if (this.flexibleSetImageFullPath) {
            carouselImages.push({
                fullPath: this.flexibleSetImageFullPath,
                isFreeInSet: false, includedQuantity: 0, alt: this.altTagForFlexibleSetImage
            });
        }

        bricksWithImages.forEach((item, index) => {
            carouselImages.push({
                fullPath: item.imageFullPath, isFreeInSet: item.isFreeInSet,
                includedQuantity: item.includedQuantity, alt: this.getAltValue(index)
            });
        });
        const finalImages: MixedMediaForCarousel[] = [];

        for (let i = 0; i < carouselImages.length; i += gridSize) {
            const slideImages = carouselImages.slice(i, i + gridSize);
            finalImages.push({ index: finalImages.length, images: slideImages });
        }

        if (isSmall) {
            finalImages.forEach((item) => {
                item.images.forEach((image) =>
                    image.fullPath = image.fullPath?.replace('Original', 'SmallTile'));
            });
            return finalImages;
        }

        finalImages.forEach((item) => {
            if (item.images.length === 1) {
                item.images[0].fullPath = item.images[0].fullPath?.replace('Original', 'FullHeight');
            }
            else if (item.images.length < 6) {
                item.images.forEach((image, index) => {
                    item.images[index].fullPath = image.fullPath?.replace('Original', 'HalfTile');
                });
            }
            else {
                item.images.forEach((image, index) => {
                    item.images[index].fullPath = image.fullPath?.replace('Original', 'SmallTile');
                });
            }
        })

        return finalImages;
    }

    private getBricksWithImages(): IMixedMediaOfBrick[] {
        const bricksWithImages = [
            ...this.mixedMediaBricks.filter(brick => (
                brick.additionalInfoImagesFullPaths?.length ||
                brick.imageFullPath ||
                brick.mainActionImageFullPath ||
                brick.detailedImageFullPath ||
                brick.secondaryImageFullPath
            ))
        ];
        return bricksWithImages;
    }
}

interface MixedMediaForCarousel {
    index: number;
    images: MixedMediaImage[];
}

interface MixedMediaImage {
    fullPath?: string;
    isFreeInSet: boolean;
    includedQuantity: number;
    alt?: string;
}
